"use client";

import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";
import { titleCasePreserveAcronyms } from "@/lib/string/title-case";

/** Import-style origins are the `upload_*` family (CSV / people import). */
export function isImportOrigin(origin?: string | null): boolean {
  return !!origin && origin.startsWith("upload_");
}

/**
 * Whether a contact action on this profile should read "Enrich" instead of
 * "Reveal". Talent Search (and legacy "sourced") profiles come from our search
 * pool, so we reveal them; everything else (resume/file import, ATS, future
 * sources) is data the user brought in, so we enrich it.
 */
export function isEnrichOrigin(origin?: string | null): boolean {
  if (!origin) return false;
  return origin !== "search" && origin !== "sourced";
}

/** Human label for a profile origin, or `null` when there's no origin. */
export function getOriginLabel(origin?: string | null): string | null {
  if (origin === "search") return "Talent Search";
  if (isImportOrigin(origin)) return "Profile Import";
  if (origin === "upload") return "Resume Upload";
  return origin ? titleCasePreserveAcronyms(origin) : null;
}

export interface ProfileOriginProps {
  origin?: string | null;
  /** Icon size for the search-origin glyph. */
  iconSize?: number;
  className?: string;
}

/**
 * Shared "profile origin" label: a search glyph + gradient text for Talent
 * Search, a shimmering purple gradient for Profile Import, and plain white for
 * everything else. Renders `null` when there's no origin. Callers that need a
 * caption (e.g. the pipeline profile's "Profile Origin" field) wrap this.
 */
export function ProfileOrigin({
  origin,
  iconSize = 18,
  className,
}: ProfileOriginProps) {
  const label = getOriginLabel(origin);
  if (!label) return null;

  const isSearch = origin === "search";
  const isImport = isImportOrigin(origin);

  return (
    <div className={cn("flex items-center gap-1.5", className)}>
      {isSearch && (
        <Icon
          name="IconSearchIntelligence"
          size={iconSize}
          className="text-ai"
        />
      )}
      {isImport ? (
        <span
          className="text-sm font-normal animate-bg-shine"
          style={{
            backgroundImage:
              "linear-gradient(110deg, #A78BFA 0%, #DDD6FE 30%, #A78BFA 35%, #A78BFA 100%)",
            backgroundSize: "200% 100%",
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            color: "transparent",
          }}
        >
          {label}
        </span>
      ) : (
        <span
          className="text-sm font-normal"
          style={
            isSearch
              ? {
                  backgroundImage:
                    "linear-gradient(90deg, #FFDB8E 32%, #FFDFB2 42%, #FFAF53 64%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }
              : { color: "white" }
          }
        >
          {label}
        </span>
      )}
    </div>
  );
}
